import { ConflictError, ForbiddenError, FromBody, FromQuery, HttpStatusCodes, Inject, NotFoundError, Result, Route } from '@resourceful-hono/core';
import { BaseResource } from '../../../BaseResource.ts';
import { GET_FILTER_SCHEMA, POST_SCHEMA } from './schema.ts';
import type { z } from 'zod';
import { CashTransaction, CashTransactionRequest, CashTransactionRequestResponse, Game } from '../../../../../entities/index.ts';
import { Protected } from '../../../common/decorators.ts';
import { Permissions } from '../../../../../services/OAuth/enums.ts';
import { APPROVAL_THRESHOLD } from './constants.ts';
import type { User } from '../../../../../entities/User.ts';
import { uniqueFailed } from '../../../../../entities/common/utils.ts';
import { GameEvent } from '../../../common/events.ts';
import { EventService } from '../../../../../services/Events.ts';

@Route('transactions/requests/responses')
export class TransactionRequestResponsesResource extends BaseResource {
  @Inject()
  declare private readonly gameEvents: EventService;

  @Protected([Permissions.GetTransactionRequestResponse])
  public async GET(
    @FromQuery(GET_FILTER_SCHEMA) filter: z.infer<typeof GET_FILTER_SCHEMA>
  ) {
    const transactionRequestResponses = await CashTransactionRequestResponse
      .find({
        where: {
          request: {
            id: filter.request,
          },
        },
        order: {
          createdAt: 'DESC',
        },
      });
    
    return Result(HttpStatusCodes.Ok, transactionRequestResponses);
  }

  @Protected([Permissions.PostTransactionRequestResponse])
  public async POST(@FromBody(POST_SCHEMA) body: z.infer<typeof POST_SCHEMA>) {
    if (!this.userId)
      throw new ForbiddenError();
    const request = await CashTransactionRequest.findOne({
      where: {
        id: body.request,
      },
      relations: {
        debitor: true,
        creditor: true,
        responses: true,
      },
    });

    if (!request)
      throw new NotFoundError(
        `Transaction request with ID ${body.request} doesn't exit`
      );

    const approvalRate = await request.approvalRate;
    if (approvalRate > APPROVAL_THRESHOLD)
      throw new ForbiddenError('Transaction request has been closed.');

    const transactionRequestResponse = new CashTransactionRequestResponse();
    transactionRequestResponse.approved = body.approved;
    transactionRequestResponse.request = request;
    transactionRequestResponse.user = {
      id: this.userId,
    } as User;

    await transactionRequestResponse.save()
      .catch((e) => {
        if (uniqueFailed(e))
          throw new ConflictError('You cannot cast multiple responses.');
      });

    request.responses.push(transactionRequestResponse);

    const game = await Game.findOne({
      where: {
        players: {
          id: this.userId,
        },
      },
    });

    if (game) {
      // publish to client
      this.gameEvents.dispatchEvent(
        new GameEvent({
          gameId: game.id,
          target: this.route.split('/'),
        })
      );
    }

    try {
      // this can fail, we don't really care
      const approvalRate = await request.approvalRate;
      if (approvalRate > APPROVAL_THRESHOLD) {
        if (game) {
          const lastTransactionId = await request
            .creditor
            .getLastTransactionId(game?.id);
          await CashTransaction.create({
            amount: request.amount,
            note: request.note,
            debitor: request.debitor,
            creditor: request.creditor,
            lastTransactionId,
            game,
          }).save();
        }
      }
    } catch {
      // TODO: log the error
    }
    
    return Result(HttpStatusCodes.Created);
  }
}